/**
 * Parsers para importação de projetos de sistemas externos.
 * Cada parser converte o formato nativo do sistema para o formato
 * esperado por ProjectContext.importProject().
 */

import Papa from 'papaparse';
import type { Task, Project, KanbanColumn } from '../types/database';
import { suggestedCategory } from '../config/kanbanCategories';
import { fill } from '../i18n';
import { plural } from '../i18n/format';
import ptModals from '../i18n/locales/pt/modals';

// =====================================================
// Tipos internos do parser
// =====================================================

export type ImportSource = 'trello' | 'clickup' | 'azure_devops' | 'ms_project';

/**
 * Textos que o parser DEVOLVE para a tela (avisos, erros e nomes provisórios).
 *
 * Chegam por parâmetro, e não por hook, para o parser continuar sendo função
 * pura: o `ImportDialog` passa `useCopy().modals.import.parser` e os testes
 * usam o padrão em português sem montar provider nenhum.
 *
 * ATENÇÃO: os APELIDOS DE CABEÇALHO abaixo (`Nome`, `Início`, `Task Name`,
 * `Fecha de inicio`...) NÃO entram aqui. Eles são DADO que vem do arquivo do
 * usuário, não interface: um cliente brasileiro com a interface em inglês
 * continua importando a planilha dele em português. Por isso as três línguas
 * convivem na mesma lista de sinônimos, sempre.
 */
export type ImportParserCopy = typeof ptModals.import.parser;

const DEFAULT_COPY: ImportParserCopy = ptModals.import.parser;

export interface ImportResult {
  project: Partial<Project>;
  tasks: Array<Partial<Task>>;
  warnings: string[];
  /**
   * Colunas do quadro que vieram da ferramenta de origem.
   *
   * Só o Trello preenche isto por ora, e é o ponto inteiro da coisa: quem sai
   * do Trello sai com um QUADRO, não com uma EAP. Sem este eixo, o importador
   * transformava cada lista numa tarefa de grupo, não escrevia status nenhum,
   * e o sujeito chegava no MeuGantt com todos os cartões empilhados na
   * primeira coluna, tendo que remontar o quadro na mão.
   */
  columns?: KanbanColumn[];
}

interface ParsedTask {
  tempId: string;
  name: string;
  type: 'task' | 'milestone' | 'group';
  start_date?: string;
  end_date?: string;
  duration?: string;
  progress?: number;
  assigned?: string;
  additionalAssignees?: string[];
  description?: string;
  color?: string;
  parentTempId?: string;
  dependencyTempIds?: string[];
  position: number;
  /** Id da coluna do quadro (vira `tasks.status`). */
  status?: string;
  /** Checklist, etiquetas e afins (vira `tasks.custom_fields`). */
  custom_fields?: Record<string, unknown>;
}

// =====================================================
// Utilitários compartilhados
// =====================================================

function sanitize(text: string | undefined | null, maxLen = 500): string {
  if (!text) return '';
  return text.replace(/<[^>]*>/g, '').trim().slice(0, maxLen);
}

/** Conta com o plural certo do idioma: `pluralFill(3, copy.x)` → "3 tarefas". */
function pluralFill(count: number, forms: { one: string; other: string }): string {
  return fill(plural(count, forms), { count });
}

/**
 * Caixa baixa E sem acento, para casar cabeçalho de planilha.
 *
 * "Início", "INICIO" e "inicio" são o mesmo cabeçalho para o usuário; sem isso,
 * quem exporta do Excel com acento e quem exporta sem cairiam em ramos
 * diferentes. Vale para os dois lados da comparação (cabeçalho e apelido).
 */
function normalize(text: string): string {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
}

/**
 * Casa cabeçalhos do arquivo com apelidos conhecidos, na ordem dos apelidos.
 *
 * A lista de apelidos é TRILÍNGUE de propósito: o mesmo arquivo pode vir de uma
 * exportação em português, inglês ou espanhol, e o idioma da INTERFACE não diz
 * nada sobre o idioma do ARQUIVO. Apelidos novos entram no fim, para nunca
 * roubarem um cabeçalho que um apelido antigo já casava.
 */
function makeHeaderFinder(headers: string[]): (aliases: string[]) => string {
  const normalized = headers.map(normalize);
  return (aliases: string[]): string => {
    for (const alias of aliases) {
      const needle = normalize(alias);
      const idx = normalized.findIndex(h => h.includes(needle));
      if (idx >= 0) return headers[idx];
    }
    return '';
  };
}

function formatDate(d: Date | string | undefined | null): string | undefined {
  if (!d) return undefined;
  const date = typeof d === 'string' ? new Date(d) : d;
  if (isNaN(date.getTime())) return undefined;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// MSPDI booleans são xsd:boolean: aceitar tanto '1'/'0' (MS Project desktop)
// quanto 'true'/'false' (exportadores de terceiros e arquivos editados à mão).
function isMSPTrue(v: string | null): boolean {
  return v === '1' || v?.toLowerCase() === 'true';
}

function buildImportResult(
  projectName: string,
  projectDescription: string,
  parsedTasks: ParsedTask[],
  warnings: string[],
  columns?: KanbanColumn[]
): ImportResult {
  // Converter ParsedTask[] para o formato Task parcial com hierarquia.
  // dependencyTempIds é transiente: importProject o resolve para UUIDs reais via
  // idMapping num 2º passe (dependencies só fica com UUIDs reais).
  type ImportNode = Partial<Task> & { children: Array<Partial<Task>>; dependencyTempIds?: string[] };
  const taskMap = new Map<string, ImportNode>();
  const rootTasks: Array<Partial<Task>> = [];

  // Criar todos os tasks primeiro
  for (const pt of parsedTasks) {
    const task: ImportNode = {
      id: pt.tempId,
      name: pt.name,
      type: pt.type,
      start_date: pt.start_date || undefined,
      end_date: pt.end_date || undefined,
      duration: pt.duration || undefined,
      progress: pt.progress || 0,
      assigned: pt.assigned || undefined,
      description: pt.description || '',
      color: pt.color || undefined,
      parent_id: pt.parentTempId || undefined,
      position: pt.position,
      expanded: true,
      dependencies: [],
      dependencyTempIds: pt.dependencyTempIds,
      status: pt.status,
      custom_fields: pt.custom_fields,
      children: [],
    };
    taskMap.set(pt.tempId, task);
  }

  // Montar hierarquia
  for (const pt of parsedTasks) {
    const task = taskMap.get(pt.tempId)!;
    if (pt.parentTempId && taskMap.has(pt.parentTempId)) {
      taskMap.get(pt.parentTempId)!.children.push(task);
    } else {
      rootTasks.push(task);
    }
  }

  return {
    project: {
      name: sanitize(projectName, 200),
      description: sanitize(projectDescription, 2000),
    },
    tasks: rootTasks,
    warnings,
    columns,
  };
}

// =====================================================
// 1. TRELLO PARSER (JSON)
// =====================================================


export function parseTrelloJSON(raw: any, copy: ImportParserCopy = DEFAULT_COPY): ImportResult {
  const warnings: string[] = [];

  if (!raw || typeof raw !== 'object') {
    throw new Error(copy.errors.trelloInvalidJson);
  }

  // Trello export has: name, lists[], cards[], members[], checklists[]
  const boardName = raw.name || copy.trelloProject;
  const boardDesc = raw.desc || '';

  const lists: any[] = raw.lists || [];
  const cards: any[] = raw.cards || [];
  const members: any[] = raw.members || [];
  const checklists: any[] = raw.checklists || [];

  if (lists.length === 0 && cards.length === 0) {
    throw new Error(copy.errors.trelloEmpty);
  }

  // Map member IDs to names
  const memberMap = new Map<string, string>();
  for (const m of members) {
    memberMap.set(m.id, m.fullName || m.username || copy.unknownMember);
  }

  // Map checklist IDs to their items
  const checklistMap = new Map<string, any[]>();
  for (const cl of checklists) {
    checklistMap.set(cl.id, cl.checkItems || []);
  }

  // Filter active lists (not archived)
  const activeLists = lists.filter((l: any) => !l.closed);
  // Filter active cards
  const activeCards = cards.filter((c: any) => !c.closed);

  const parsedTasks: ParsedTask[] = [];
  let position = 0;

  // Sort lists by position
  activeLists.sort((a: any, b: any) => (a.pos || 0) - (b.pos || 0));

  /**
   * Lista do Trello vira COLUNA do quadro, e não tarefa de grupo.
   *
   * Era o defeito que esvaziava a feature inteira: o importador transformava
   * "A Fazer / Fazendo / Publicado" numa EAP de três grupos, não escrevia
   * `tasks.status` em lugar nenhum, e o quadro abria com todo cartão empilhado
   * na primeira coluna. A pessoa migrava do Trello e a primeira coisa que via
   * era o trabalho de remontar o quadro na mão.
   *
   * A categoria vem da POSIÇÃO, que é a única informação que o Trello tem
   * sobre significado: ele não guarda nada equivalente. Primeira lista é
   * entrada, última é saída, o meio é trabalho em curso. Fica visível e
   * editável no editor de colunas logo depois, que é exatamente o antídoto
   * para o modo de falha da importação Trello-para-Jira, onde a coluna cai
   * calada numa categoria e o usuário só descobre pelo relatório errado.
   */
  const columns: KanbanColumn[] = activeLists.map((list: any, i: number) => ({
    id: `col_${i + 1}`,
    name: sanitize(list.name, 60) || `${i + 1}`,
    color: trelloColorToHex(list.color) || COLUNA_CORES[i % COLUNA_CORES.length],
    position: i,
    wipLimit: 0,
    category: suggestedCategory(i, activeLists.length),
  }));

  for (const [indiceLista, list] of activeLists.entries()) {
    const colunaId = columns[indiceLista].id;

    // Cards in this list
    const listCards = activeCards
      .filter((c: any) => c.idList === list.id)
      .sort((a: any, b: any) => (a.pos || 0) - (b.pos || 0));

    for (const card of listCards) {
      const cardId = `trello-card-${card.id}`;

      // Get assigned members (primary + additional)
      let assigned: string | undefined;
      let additionalAssignees: string[] | undefined;
      if (card.idMembers && card.idMembers.length > 0) {
        assigned = memberMap.get(card.idMembers[0]);
        if (card.idMembers.length > 1) {
          additionalAssignees = card.idMembers.slice(1)
            .map((id: string) => memberMap.get(id))
            .filter(Boolean) as string[];
        }
      }

      // Get color from first label
      let color: string | undefined;
      if (card.labels && card.labels.length > 0) {
        const labelColor = card.labels[0].color;
        color = trelloColorToHex(labelColor);
      }

      /**
       * Checklist do Trello vira CHECKLIST do cartão, e não neto na árvore.
       *
       * Antes cada item virava uma tarefa filha do cartão, e o quadro renderiza
       * folhas: o cartão aparecia lado a lado com cada item de checklist dele,
       * como se fossem cartões irmãos. O mapeamento certo é 1 para 1, e o card
       * do MeuGantt já tem checklist em `custom_fields`.
       */
      const checklist: Array<{ id: string; text: string; done: boolean }> = [];
      for (const clId of (card.idChecklists || [])) {
        for (const item of (checklistMap.get(clId) || [])) {
          checklist.push({
            id: `trello-check-${item.id}`,
            text: sanitize(item.name, 200),
            done: item.state === 'complete',
          });
        }
      }

      // Cartão só de checklist já concluído chega com o progresso que o
      // Trello de fato conhecia. NÃO deduzimos progresso da coluna: isso é
      // trabalho do usuário mover o cartão, e inventar aqui mentiria no EVM.
      const concluidos = checklist.filter(c => c.done).length;
      const progresso = checklist.length > 0
        ? Math.round((concluidos / checklist.length) * 100)
        : undefined;

      parsedTasks.push({
        tempId: cardId,
        name: sanitize(card.name, 200),
        type: 'task',
        end_date: formatDate(card.due),
        assigned,
        additionalAssignees,
        description: sanitize(card.desc, 2000),
        color,
        // Raiz, e não filha de um grupo: o cartão do Trello é um cartão.
        position: position++,
        status: colunaId,
        progress: progresso,
        custom_fields: checklist.length > 0 ? { checklist } : undefined,
      });
    }
  }

  warnings.push(copy.warnings.trelloNoDependencies);
  if (activeCards.length > 100) {
    warnings.push(pluralFill(activeCards.length, copy.warnings.trelloManyCards));
  }

  return buildImportResult(boardName, boardDesc, parsedTasks, warnings, columns);
}

/** Cores de reserva para lista do Trello que não tem cor própria. */
const COLUNA_CORES = ['#94a3b8', '#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'];

function trelloColorToHex(color: string | undefined): string | undefined {
  const colorMap: Record<string, string> = {
    green: '#61bd4f',
    yellow: '#f2d600',
    orange: '#ff9f1a',
    red: '#eb5a46',
    purple: '#c377e0',
    blue: '#0079bf',
    sky: '#00c2e0',
    lime: '#51e898',
    pink: '#ff78cb',
    black: '#344563',
  };
  return color ? colorMap[color] : undefined;
}

// =====================================================
// 2. CLICKUP PARSER (CSV)
// =====================================================

export function parseClickUpCSV(csvText: string, copy: ImportParserCopy = DEFAULT_COPY): ImportResult {
  const warnings: string[] = [];

  const result = Papa.parse(csvText, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (h: string) => h.trim(),
  });

  if (result.errors.length > 0) {
    const critical = result.errors.filter((e: any) => e.type === 'FieldMismatch' || e.type === 'Delimiter');
    if (critical.length > 0) {
      throw new Error(fill(copy.errors.clickupCsv, { message: critical[0].message }));
    }
  }

  const rows = result.data as Record<string, string>[];
  if (rows.length === 0) {
    throw new Error(copy.errors.clickupEmpty);
  }

  // Auto-detect column names (ClickUp uses various names)
  const headers = result.meta.fields || [];
  const colMap = detectClickUpColumns(headers);

  const parsedTasks: ParsedTask[] = [];
  const parentMap = new Map<string, string>(); // task name → tempId (for parent matching)
  let position = 0;

  for (const row of rows) {
    const name = sanitize(row[colMap.name], 200);
    if (!name) continue;

    const tempId = `clickup-${position}`;

    // Detect parent
    let parentTempId: string | undefined;
    if (colMap.parent && row[colMap.parent]) {
      const parentName = row[colMap.parent].trim();
      parentTempId = parentMap.get(parentName);
    }

    // Detect type
    let type: 'task' | 'milestone' | 'group' = 'task';
    if (colMap.type && row[colMap.type]) {
      const t = row[colMap.type].toLowerCase();
      if (t === 'milestone') type = 'milestone';
    }

    // Priority mapping
    let priority: number | undefined;
    if (colMap.priority && row[colMap.priority]) {
      const p = row[colMap.priority].toLowerCase();
      if (p === 'urgent') priority = 4;
      else if (p === 'high') priority = 3;
      else if (p === 'normal') priority = 2;
      else if (p === 'low') priority = 1;
    }

    parsedTasks.push({
      tempId,
      name,
      type,
      start_date: formatDate(row[colMap.startDate]),
      end_date: formatDate(row[colMap.dueDate]),
      duration: parseClickUpTimeEstimate(row[colMap.timeEstimate]),
      progress: parseProgress(row[colMap.status]),
      status: parseStatusColuna(row[colMap.status]),
      assigned: sanitize(row[colMap.assignee], 100),
      description: sanitize(row[colMap.description], 2000),
      parentTempId,
      position: position,
    });

    parentMap.set(name, tempId);
    position++;
  }

  warnings.push(copy.warnings.clickupNoDependencies);

  return buildImportResult(
    pluralFill(rows.length, copy.clickupProject),
    '',
    parsedTasks,
    warnings
  );
}

/**
 * Apelidos de cabeçalho do ClickUp, nas TRÊS línguas ao mesmo tempo.
 *
 * O idioma da interface não entra aqui: a planilha vem do ClickUp do usuário e
 * pode estar em qualquer idioma. Os apelidos em português são os originais e
 * não saem nunca; inglês e espanhol foram ACRESCENTADOS no fim de cada lista,
 * onde não roubam um cabeçalho que um apelido anterior já casava.
 */
function detectClickUpColumns(headers: string[]): Record<string, string> {
  const find = makeHeaderFinder(headers);

  return {
    name: find(['task name', 'name', 'nome', 'título', 'title', 'nombre', 'tarea']) || headers[0] || 'Task Name',
    status: find(['status', 'estado', 'situação']),
    priority: find(['priority', 'prioridade', 'prioridad']),
    assignee: find(['assignee', 'assignees', 'responsável', 'responsavel', 'responsable', 'asignado']),
    startDate: find(['start date', 'data início', 'data inicio', 'data de início', 'fecha de inicio', 'fecha inicio']),
    dueDate: find(['due date', 'end date', 'data fim', 'data término', 'data de término', 'fecha de vencimiento', 'fecha fin', 'fecha límite', 'deadline']),
    timeEstimate: find(['time estimate', 'estimativa', 'estimate', 'tiempo estimado', 'estimación']),
    description: find(['description', 'descrição', 'descricao', 'descripción']),
    parent: find(['parent', 'parent task', 'pai', 'tarefa pai', 'padre', 'tarea padre']),
    type: find(['type', 'tipo']),
    tags: find(['tags', 'labels', 'etiquetas']),
  };
}

function parseClickUpTimeEstimate(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  // Already in "Xd" format
  if (/^\d+d$/i.test(trimmed)) return trimmed.toLowerCase();
  // Hours format like "8h" or "8 hours"
  const hoursMatch = trimmed.match(/^(\d+)\s*h/i);
  if (hoursMatch) {
    const hours = parseInt(hoursMatch[1]);
    const days = Math.ceil(hours / 8);
    return `${days}d`;
  }
  // Minutes
  const minMatch = trimmed.match(/^(\d+)\s*m/i);
  if (minMatch) return '1d';
  return undefined;
}

/**
 * Status do arquivo do usuário → percentual. Os valores vêm do ClickUp dele, em
 * qualquer idioma, então as três línguas convivem na mesma lista. Os valores em
 * português são os originais e continuam valendo.
 */
const DONE_STATUSES = new Set([
  'complete', 'done', 'closed',
  'concluido', 'concluida', 'finalizado', 'fechado',
  'completado', 'completada', 'terminado', 'cerrado',
]);

const IN_PROGRESS_STATUSES = new Set([
  'in progress', 'in review',
  'em andamento', 'em progresso', 'em revisao',
  'en progreso', 'en curso', 'en revision',
]);

function parseProgress(status: string | undefined): number {
  if (!status) return 0;
  const s = normalize(status);
  if (DONE_STATUSES.has(s)) return 100;
  if (IN_PROGRESS_STATUSES.has(s)) return 50;
  return 0;
}

/**
 * O mesmo texto de status da origem, agora traduzido também para a COLUNA.
 *
 * Antes, o texto de status só virava progresso: a tarefa importada nascia com
 * `status` nulo (e o serviço aplicava 'not_started'), então o Gantt mostrava
 * "Concluída" pelo percentual e o Kanban mostrava "Não iniciada" pelo status,
 * para a mesma linha. Devolve os ids CANÔNICOS porque estas ferramentas não
 * trazem quadro: o projeto importado fica com as cinco colunas semeadas.
 */
function parseStatusColuna(status: string | undefined): string | undefined {
  if (!status) return undefined;
  const s = normalize(status);
  if (DONE_STATUSES.has(s)) return 'completed';
  if (IN_PROGRESS_STATUSES.has(s)) return 'in_progress';
  return 'not_started';
}

// =====================================================
// 3. AZURE DEVOPS PARSER (CSV)
// =====================================================

/**
 * Tipos e estados de work item do Azure DevOps. O Azure entrega os valores do
 * PROCESS TEMPLATE da organização, que pode estar localizado, então as três
 * línguas convivem na mesma lista (comparação já sem acento e sem caixa).
 */
const AZURE_GROUP_TYPES = new Set([
  'epic', 'feature',
  'epico', 'epica', 'funcionalidade', 'caracteristica',
]);

const AZURE_DONE_STATES = new Set([
  'closed', 'done', 'resolved',
  'fechado', 'concluido', 'resolvido',
  'cerrado', 'completado', 'resuelto', 'terminado',
]);

const AZURE_ACTIVE_STATES = new Set([
  'active', 'committed',
  'ativo', 'em andamento', 'comprometido',
  'activo', 'en curso',
]);

export function parseAzureDevOpsCSV(csvText: string, copy: ImportParserCopy = DEFAULT_COPY): ImportResult {
  const warnings: string[] = [];

  const result = Papa.parse(csvText, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (h: string) => h.trim(),
  });

  if (result.errors.length > 0) {
    const critical = result.errors.filter((e: any) => e.type !== 'FieldMismatch');
    if (critical.length > 0) {
      throw new Error(fill(copy.errors.azureCsv, { message: critical[0].message }));
    }
  }

  const rows = result.data as Record<string, string>[];
  if (rows.length === 0) {
    throw new Error(copy.errors.azureEmpty);
  }

  const headers = result.meta.fields || [];
  const colMap = detectAzureColumns(headers);

  const parsedTasks: ParsedTask[] = [];
  const workItemIdMap = new Map<string, string>(); // Work Item ID → tempId
  let position = 0;

  for (const row of rows) {
    const title = sanitize(row[colMap.title], 200);
    if (!title) continue;

    const tempId = `azure-${position}`;
    const workItemId = row[colMap.id]?.trim();
    if (workItemId) {
      workItemIdMap.set(workItemId, tempId);
    }

    // Map Work Item Type to task type
    const wiType = normalize(row[colMap.type] || '');
    let type: 'task' | 'milestone' | 'group' = 'task';
    if (AZURE_GROUP_TYPES.has(wiType)) {
      type = 'group';
    }

    // Map State to progress
    const state = normalize(row[colMap.state] || '');
    let progress = 0;
    if (AZURE_DONE_STATES.has(state)) progress = 100;
    else if (AZURE_ACTIVE_STATES.has(state)) progress = 50;

    // Parent mapping
    let parentTempId: string | undefined;
    const parentId = row[colMap.parent]?.trim();
    if (parentId && workItemIdMap.has(parentId)) {
      parentTempId = workItemIdMap.get(parentId);
    }

    // Duration from estimate
    let duration: string | undefined;
    const estimate = row[colMap.estimate]?.trim();
    if (estimate) {
      const hours = parseFloat(estimate);
      if (!isNaN(hours) && hours > 0) {
        duration = `${Math.ceil(hours / 8)}d`;
      }
    }

    parsedTasks.push({
      tempId,
      name: title,
      type,
      start_date: formatDate(row[colMap.startDate]),
      end_date: formatDate(row[colMap.dueDate]),
      duration,
      progress,
      assigned: sanitize(row[colMap.assigned], 100),
      description: sanitize(row[colMap.description], 2000),
      parentTempId,
      position: position,
    });

    position++;
  }

  warnings.push(copy.warnings.azureNoDependencies);
  if (colMap.iteration && rows.some(r => r[colMap.iteration])) {
    warnings.push(copy.warnings.azureIterations);
  }

  return buildImportResult(
    pluralFill(rows.length, copy.azureProject),
    '',
    parsedTasks,
    warnings
  );
}

/**
 * Mesma regra do ClickUp: apelidos trilíngues, portugueses preservados, novos
 * no fim de cada lista.
 */
function detectAzureColumns(headers: string[]): Record<string, string> {
  const find = makeHeaderFinder(headers);

  return {
    id: find(['id', 'work item id']),
    title: find(['title', 'título', 'titulo', 'nombre', 'asunto']) || headers[0] || 'Title',
    type: find(['work item type', 'type', 'tipo', 'tipo de elemento de trabajo']),
    state: find(['state', 'status', 'estado']),
    assigned: find(['assigned to', 'assigned', 'responsável', 'responsavel', 'asignado a', 'asignado', 'responsable']),
    startDate: find(['start date', 'data início', 'data de início', 'fecha de inicio', 'fecha inicio']),
    dueDate: find(['target date', 'due date', 'finish date', 'data fim', 'data término', 'fecha objetivo', 'fecha de vencimiento', 'fecha fin']),
    estimate: find(['original estimate', 'effort', 'story points', 'estimativa', 'esfuerzo', 'estimación original', 'puntos de historia']),
    description: find(['description', 'descrição', 'descricao', 'descripción']),
    parent: find(['parent', 'parent id', 'pai', 'padre']),
    iteration: find(['iteration path', 'iteration', 'sprint', 'ruta de iteración', 'iteración']),
    area: find(['area path', 'area', 'área', 'ruta de área']),
  };
}

// =====================================================
// 4. MS PROJECT PARSER (XML)
// =====================================================

export function parseMSProjectXML(xmlText: string, copy: ImportParserCopy = DEFAULT_COPY): ImportResult {
  const warnings: string[] = [];

  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlText, 'text/xml');

  // Check for parse errors
  const parseError = doc.querySelector('parsererror');
  if (parseError) {
    throw new Error(copy.errors.mspInvalidXml);
  }

  // Get project info
  const projectEl = doc.querySelector('Project');
  if (!projectEl) {
    throw new Error(copy.errors.mspNoProjectElement);
  }

  const projectName = getXmlText(projectEl, 'Name') || getXmlText(projectEl, 'Title') || copy.msProject;

  // Parse resources for assignment mapping
  const resourceMap = new Map<string, string>(); // ResourceUID → Name
  const resources = doc.querySelectorAll('Project > Resources > Resource');
  for (const res of resources) {
    const uid = getXmlText(res, 'UID');
    const name = getXmlText(res, 'Name');
    if (uid && name) {
      resourceMap.set(uid, name);
    }
  }

  // Parse assignments for task → resource mapping
  const taskAssignments = new Map<string, string>(); // TaskUID → Resource Name
  const assignments = doc.querySelectorAll('Project > Assignments > Assignment');
  for (const asgn of assignments) {
    const taskUID = getXmlText(asgn, 'TaskUID');
    const resourceUID = getXmlText(asgn, 'ResourceUID');
    if (taskUID && resourceUID && !taskAssignments.has(taskUID)) {
      const resourceName = resourceMap.get(resourceUID);
      if (resourceName) {
        taskAssignments.set(taskUID, resourceName);
      }
    }
  }

  // Parse tasks
  const taskElements = doc.querySelectorAll('Project > Tasks > Task');
  const parsedTasks: ParsedTask[] = [];
  const uidToTempId = new Map<string, string>(); // UID → tempId
  const predUIDsByTempId = new Map<string, string[]>(); // tempId → PredecessorUIDs crus (resolvidos no 2º passe)
  const outlineLevelStack: string[] = []; // Stack of parent tempIds by outline level
  let position = 0;
  let unnamedCount = 0;

  for (const taskEl of taskElements) {
    const uid = getXmlText(taskEl, 'UID');
    // Skip UID 0 (root summary task)
    if (uid === '0') continue;
    // Pular linhas em branco/placeholder do MS Project (IsNull=1): NÃO empilhar,
    // senão a hierarquia das tarefas seguintes seria corrompida.
    if (isMSPTrue(getXmlText(taskEl, 'IsNull'))) continue;

    // Nome: se vazio (whitespace/markup), NÃO pular. O 'continue' antigo rodava
    // ANTES da pilha de OutlineLevel e reparenteava os filhos da tarefa pulada
    // para o grupo irmão errado (ou os transformava em raízes), sem aviso.
    let name = sanitize(getXmlText(taskEl, 'Name'), 200);
    if (!name) {
      name = fill(copy.unnamedTask, { id: uid || position + 1 });
      unnamedCount++;
    }

    const tempId = `msp-${uid}`;
    uidToTempId.set(uid!, tempId);

    // Tipo: Summary => grupo, Milestone => marco (booleans 1/0 ou true/false).
    const isSummary = isMSPTrue(getXmlText(taskEl, 'Summary'));
    const isMilestone = isMSPTrue(getXmlText(taskEl, 'Milestone'));
    let type: 'task' | 'milestone' | 'group' = 'task';
    if (isSummary) type = 'group';
    else if (isMilestone) type = 'milestone';

    // Dates
    const startStr = getXmlText(taskEl, 'Start');
    const finishStr = getXmlText(taskEl, 'Finish');
    const start_date = formatDate(startStr);
    const end_date = formatDate(finishStr);

    // Duration (ISO 8601: PT8H0M0S → "1d")
    const durationStr = getXmlText(taskEl, 'Duration');
    const duration = parseMSProjectDuration(durationStr);

    // Progress
    const percentComplete = parseInt(getXmlText(taskEl, 'PercentComplete') || '0');
    const progress = isNaN(percentComplete) ? 0 : Math.min(100, Math.max(0, percentComplete));

    // Assigned resource
    const assigned = uid ? taskAssignments.get(uid) : undefined;

    // Description from Notes
    const description = sanitize(getXmlText(taskEl, 'Notes'), 2000);

    // Hierarchy via OutlineLevel. Clampar para >= 1: um literal '0' (ou negativo,
    // ou NaN de arquivo malformado) faria 'while (stack.length >= outlineLevel)'
    // virar loop infinito (freeze da aba — o parse é síncrono na thread principal)
    // ou 'stack.length = NaN' lançar RangeError. Math.max(1, ... || 1) cobre tudo.
    const outlineLevel = Math.max(1, parseInt(getXmlText(taskEl, 'OutlineLevel') || '1') || 1);
    let parentTempId: string | undefined;

    // Adjust stack to current level
    while (outlineLevelStack.length >= outlineLevel) {
      outlineLevelStack.pop();
    }
    if (outlineLevelStack.length > 0) {
      parentTempId = outlineLevelStack[outlineLevelStack.length - 1];
    }
    outlineLevelStack[outlineLevel - 1] = tempId;
    // Trim stack to current level
    outlineLevelStack.length = outlineLevel;

    // Dependencies (PredecessorLink): coletar os PredecessorUID crus agora e
    // resolver para tempIds DEPOIS do loop (uidToTempId completo) — senão forward
    // references (predecessor que aparece mais adiante no doc) seriam perdidas.
    const rawPredUIDs: string[] = [];
    for (const pred of taskEl.querySelectorAll('PredecessorLink')) {
      const predUID = getXmlText(pred, 'PredecessorUID');
      if (predUID && predUID !== '0') rawPredUIDs.push(predUID);
    }
    if (rawPredUIDs.length > 0) predUIDsByTempId.set(tempId, rawPredUIDs);

    parsedTasks.push({
      tempId,
      name,
      type,
      start_date,
      end_date,
      duration,
      progress,
      assigned,
      description,
      parentTempId,
      position: position,
    });

    position++;
  }

  // 2º passe: resolver dependências agora que uidToTempId está completo.
  for (const pt of parsedTasks) {
    const raw = predUIDsByTempId.get(pt.tempId);
    if (!raw) continue;
    const resolved = raw.map(u => uidToTempId.get(u)).filter((x): x is string => !!x);
    if (resolved.length > 0) pt.dependencyTempIds = resolved;
  }

  if (unnamedCount > 0) {
    warnings.push(pluralFill(unnamedCount, copy.warnings.unnamedTasks));
  }

  const depCount = parsedTasks.reduce((n, t) => n + (t.dependencyTempIds?.length || 0), 0);
  if (depCount > 0) {
    warnings.push(pluralFill(depCount, copy.warnings.dependencies));
  }

  if (parsedTasks.length === 0) {
    throw new Error(copy.errors.mspNoTasks);
  }

  return buildImportResult(projectName, '', parsedTasks, warnings);
}

function getXmlText(parent: Element, tagName: string): string | null {
  // Try direct children first, then deeper
  const el = parent.querySelector(`:scope > ${tagName}`);
  return el?.textContent?.trim() || null;
}

function parseMSProjectDuration(duration: string | null): string | undefined {
  if (!duration) return undefined;

  // Format: PT8H0M0S or PT40H0M0S or PT0H0M0S
  const match = duration.match(/PT(\d+)H(\d+)M(\d+)S/);
  if (!match) return undefined;

  const hours = parseInt(match[1]);
  const minutes = parseInt(match[2]);

  if (hours === 0 && minutes === 0) return '0d';

  const totalHours = hours + minutes / 60;
  const days = Math.max(1, Math.round(totalHours / 8));
  return `${days}d`;
}

// =====================================================
// Função principal: detecta formato e parseia
// =====================================================

export function parseImportFile(
  content: string,
  source: ImportSource,
  copy: ImportParserCopy = DEFAULT_COPY
): ImportResult {
  switch (source) {
    case 'trello':
      return parseTrelloJSON(JSON.parse(content), copy);
    case 'clickup':
      return parseClickUpCSV(content, copy);
    case 'azure_devops':
      return parseAzureDevOpsCSV(content, copy);
    case 'ms_project':
      return parseMSProjectXML(content, copy);
    default:
      throw new Error(fill(copy.errors.unsupportedSource, { source }));
  }
}

export function getAcceptedFileTypes(source: ImportSource): string {
  switch (source) {
    case 'trello':
      return '.json';
    case 'clickup':
    case 'azure_devops':
      return '.csv';
    case 'ms_project':
      return '.xml';
    default:
      return '';
  }
}

// As instruções de exportação de cada produto ("No Trello, abra o board → ...")
// são interface pura, sem regra: vivem em `modals.import.instructions` e o
// ImportDialog as lê direto do dicionário, indexadas pelo ImportSource.
